import React, { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import {
  Trash2,
  Search,
  RefreshCw,
  Mail,
  Plus,
  Download,
  Copy,
  Check,
  AlertTriangle,
  UserCheck,
  ShieldAlert,
  X,
} from "lucide-react";

export function UnsubscribeEmail() {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [copiedText, setCopiedText] = useState("");

  // Modal states
  const [deleteModalItem, setDeleteModalItem] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newEmailInput, setNewEmailInput] = useState("");
  const [submittingAdd, setSubmittingAdd] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(25);

  const BaseURL =
    process.env.REACT_APP_BASEURL || "https://api.equipmedy.com/api/v1";

  const getAuthHeaders = () => {
    const token =
      localStorage.getItem("adminAuth") || localStorage.getItem("token");
    return {
      accept: "application/json",
      ...(token ? { Authorization: `Token ${token}` } : {}),
    };
  };

  // Fetch list of unsubscribed emails
  const fetchUnsubscribedEmails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${BaseURL}/unsubscribed-email/`, {
        headers: getAuthHeaders(),
      });

      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.results || response.data?.data || [];

      setEmails(data);
    } catch (error) {
      console.error("Error fetching unsubscribed emails:", error);
      toast.error(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Failed to load unsubscribed emails."
      );
    } finally {
      setLoading(false);
    }
  }, [BaseURL]);

  useEffect(() => {
    fetchUnsubscribedEmails();
  }, [fetchUnsubscribedEmails]);

  // Copy helper
  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    toast.info(`Copied: ${text}`);
    setTimeout(() => setCopiedText(""), 2000);
  };

  // Remove / Resubscribe action
  const confirmDelete = async () => {
    if (!deleteModalItem) return;
    const { id, email } = deleteModalItem;
    setDeletingId(id);

    try {
      // 1. Try standard DELETE by ID/pk
      let deleteSuccessful = false;
      if (id) {
        try {
          await axios.delete(`${BaseURL}/unsubscribed-email/${id}/`, {
            headers: getAuthHeaders(),
          });
          deleteSuccessful = true;
        } catch (delErr) {
          console.warn("DELETE by ID failed, falling back to resubscribe action", delErr);
        }
      }

      // 2. Fallback: call resubscribe endpoint if direct delete failed
      if (!deleteSuccessful && email) {
        await axios.post(
          `${BaseURL}/unsubscribed-email/resubscribe/`,
          { email },
          {
            headers: {
              ...getAuthHeaders(),
              "Content-Type": "application/json",
            },
          }
        );
      }

      toast.success(`${email} has been removed and resubscribed!`);
      setEmails((prev) => prev.filter((item) => item.id !== id && item.email !== email));
      setDeleteModalItem(null);
    } catch (error) {
      console.error("Error removing unsubscribed email:", error);
      toast.error(
        error?.response?.data?.detail ||
          error?.response?.data?.message ||
          "Failed to remove email. Please try again."
      );
    } finally {
      setDeletingId(null);
    }
  };

  // Add new email to unsubscribe list
  const handleAddEmailSubmit = async (e) => {
    e.preventDefault();
    const cleanEmail = (newEmailInput || "").trim().toLowerCase();

    if (!cleanEmail || !cleanEmail.includes("@")) {
      toast.error("Please enter a valid email address.");
      return;
    }

    setSubmittingAdd(true);
    try {
      await axios.post(
        `${BaseURL}/unsubscribed-email/unsubscribe/`,
        { email: cleanEmail },
        {
          headers: {
            ...getAuthHeaders(),
            "Content-Type": "application/json",
          },
        }
      );

      toast.success(`${cleanEmail} added to unsubscribed list!`);
      setNewEmailInput("");
      setIsAddModalOpen(false);
      fetchUnsubscribedEmails();
    } catch (error) {
      console.error("Error adding email:", error);
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.detail ||
          "Failed to add email to unsubscribe list."
      );
    } finally {
      setSubmittingAdd(false);
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (!emails.length) {
      toast.info("No data to export.");
      return;
    }

    const headers = ["ID", "UID", "Email", "User ID"];
    const rows = filteredEmails.map((item) => [
      item.id || "",
      item.uid || "",
      item.email || "",
      item.user || "",
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," +
      [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute(
      "download",
      `unsubscribed_emails_${new Date().toISOString().split("T")[0]}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("CSV file downloaded successfully!");
  };

  // Filtered & Searched Data
  const filteredEmails = useMemo(() => {
    if (!searchQuery.trim()) return emails;
    const q = searchQuery.toLowerCase().trim();
    return emails.filter(
      (item) =>
        (item.email && item.email.toLowerCase().includes(q)) ||
        (item.id && String(item.id).includes(q)) ||
        (item.uid && item.uid.toLowerCase().includes(q)) ||
        (item.user && String(item.user).includes(q))
    );
  }, [emails, searchQuery]);

  // Paginated Data
  const totalPages = Math.ceil(filteredEmails.length / perPage) || 1;
  const paginatedEmails = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredEmails.slice(start, start + perPage);
  }, [filteredEmails, currentPage, perPage]);

  // Summary Metrics
  const metrics = useMemo(() => {
    const total = emails.length;
    const withUser = emails.filter((e) => e.user !== null && e.user !== undefined).length;
    const guest = total - withUser;
    return { total, withUser, guest };
  }, [emails]);

  return (
    <div style={styles.container}>
      {/* HEADER SECTION */}
      <div style={styles.header}>
        <div>
          <div style={styles.titleRow}>
            <div style={styles.iconBadge}>
              <ShieldAlert size={22} color="#dc2626" />
            </div>
            <h1 style={styles.title}>Unsubscribed Emails Management</h1>
            <span style={styles.badge}>{metrics.total} Opted Out</span>
          </div>
          <p style={styles.subtitle}>
            Users and email addresses that have unsubscribed from EquipMedy marketing & notification emails.
            Removing an email will <strong>resubscribe</strong> them so they can receive communications again.
          </p>
        </div>

        {/* TOP ACTIONS */}
        <div style={styles.headerActions}>
          <button
            onClick={() => setIsAddModalOpen(true)}
            style={styles.btnPrimary}
            title="Manually unsubscribe an email"
          >
            <Plus size={16} />
            <span>Add Unsubscribed</span>
          </button>

          <button
            onClick={handleExportCSV}
            style={styles.btnSecondary}
            title="Export filtered list to CSV"
          >
            <Download size={16} />
            <span>Export CSV</span>
          </button>

          <button
            onClick={fetchUnsubscribedEmails}
            disabled={loading}
            style={styles.btnIcon}
            title="Refresh list"
          >
            <RefreshCw size={16} className={loading ? "spin-animation" : ""} />
          </button>
        </div>
      </div>

      {/* METRIC CARDS */}
      <div style={styles.metricsGrid}>
        <div style={styles.metricCard}>
          <div style={styles.metricLabel}>Total Unsubscribed</div>
          <div style={{ ...styles.metricValue, color: "#dc2626" }}>
            {metrics.total}
          </div>
          <div style={styles.metricHelp}>Total active email suppressions</div>
        </div>

        <div style={styles.metricCard}>
          <div style={styles.metricLabel}>Registered Users</div>
          <div style={{ ...styles.metricValue, color: "#008080" }}>
            {metrics.withUser}
          </div>
          <div style={styles.metricHelp}>Linked to registered user accounts</div>
        </div>

        <div style={styles.metricCard}>
          <div style={styles.metricLabel}>Guest / Direct Contacts</div>
          <div style={{ ...styles.metricValue, color: "#4b5563" }}>
            {metrics.guest}
          </div>
          <div style={styles.metricHelp}>Unregistered inquiries & visitors</div>
        </div>
      </div>

      {/* SEARCH AND CONTROLS */}
      <div style={styles.controlsBar}>
        <div style={styles.searchWrapper}>
          <Search size={18} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search by email address, user ID, or UID..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setCurrentPage(1);
            }}
            style={styles.searchInput}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              style={styles.clearSearchBtn}
            >
              <X size={14} />
            </button>
          )}
        </div>

        <div style={styles.perPageSelector}>
          <label style={{ fontSize: "13px", color: "#6b7280" }}>Show:</label>
          <select
            value={perPage}
            onChange={(e) => {
              setPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            style={styles.select}
          >
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>
      </div>

      {/* MAIN TABLE CONTAINER */}
      <div style={styles.tableCard}>
        {loading ? (
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}></div>
            <p style={{ marginTop: "12px", color: "#6b7280", fontSize: "14px" }}>
              Loading unsubscribed emails...
            </p>
          </div>
        ) : filteredEmails.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIconCircle}>
              <Mail size={32} color="#008080" />
            </div>
            <h3 style={styles.emptyTitle}>
              {searchQuery ? "No matching emails found" : "No unsubscribed emails"}
            </h3>
            <p style={styles.emptyText}>
              {searchQuery
                ? `No results found for "${searchQuery}". Try a different search term.`
                : "Great news! There are currently no email addresses in the unsubscribe suppression list."}
            </p>
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                style={styles.btnSecondary}
              >
                Clear Search
              </button>
            )}
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.thRow}>
                  <th style={{ ...styles.th, width: "70px" }}>ID</th>
                  <th style={styles.th}>Email Address</th>
                  <th style={{ ...styles.th, width: "160px" }}>Linked User</th>
                  <th style={{ ...styles.th, width: "240px" }}>System UID</th>
                  <th style={{ ...styles.th, width: "130px", textAlign: "center" }}>
                    Status
                  </th>
                  <th style={{ ...styles.th, width: "180px", textAlign: "right" }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedEmails.map((item, index) => {
                  const isDeleting = deletingId === item.id;
                  return (
                    <tr
                      key={item.id || item.uid || index}
                      style={{
                        ...styles.tr,
                        backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafbfc",
                      }}
                    >
                      {/* ID */}
                      <td style={styles.td}>
                        <span style={styles.idBadge}>#{item.id || "-"}</span>
                      </td>

                      {/* EMAIL */}
                      <td style={styles.td}>
                        <div style={styles.emailContainer}>
                          <Mail size={16} color="#008080" style={{ flexShrink: 0 }} />
                          <span style={styles.emailText}>{item.email}</span>
                          <button
                            onClick={() => handleCopy(item.email, `email-${item.id}`)}
                            title="Copy email"
                            style={styles.iconMiniBtn}
                          >
                            {copiedText === `email-${item.id}` ? (
                              <Check size={13} color="#16a34a" />
                            ) : (
                              <Copy size={13} color="#9ca3af" />
                            )}
                          </button>
                        </div>
                      </td>

                      {/* USER ID */}
                      <td style={styles.td}>
                        {item.user ? (
                          <span style={styles.userBadge}>
                            <UserCheck size={13} style={{ marginRight: "4px" }} />
                            User ID: {item.user}
                          </span>
                        ) : (
                          <span style={styles.guestBadge}>Guest / Visitor</span>
                        )}
                      </td>

                      {/* UID */}
                      <td style={styles.td}>
                        <div style={styles.uidContainer}>
                          <span style={styles.uidText} title={item.uid}>
                            {item.uid || "-"}
                          </span>
                          {item.uid && (
                            <button
                              onClick={() => handleCopy(item.uid, `uid-${item.id}`)}
                              title="Copy UID"
                              style={styles.iconMiniBtn}
                            >
                              {copiedText === `uid-${item.id}` ? (
                                <Check size={12} color="#16a34a" />
                              ) : (
                                <Copy size={12} color="#9ca3af" />
                              )}
                            </button>
                          )}
                        </div>
                      </td>

                      {/* STATUS */}
                      <td style={{ ...styles.td, textAlign: "center" }}>
                        <span style={styles.unsubscribedTag}>Unsubscribed</span>
                      </td>

                      {/* DELETE / REMOVE ACTION */}
                      <td style={{ ...styles.td, textAlign: "right" }}>
                        <button
                          onClick={() => setDeleteModalItem(item)}
                          disabled={isDeleting}
                          style={styles.btnDelete}
                          title="Remove from unsubscribe list (Resubscribe)"
                        >
                          <Trash2 size={14} style={{ marginRight: "5px" }} />
                          <span>Remove</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* PAGINATION BAR */}
        {!loading && filteredEmails.length > 0 && (
          <div style={styles.paginationBar}>
            <span style={{ fontSize: "13px", color: "#6b7280" }}>
              Showing {(currentPage - 1) * perPage + 1} to{" "}
              {Math.min(currentPage * perPage, filteredEmails.length)} of{" "}
              {filteredEmails.length} entries
            </span>

            <div style={styles.pageButtons}>
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                style={{
                  ...styles.pageBtn,
                  opacity: currentPage === 1 ? 0.4 : 1,
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                }}
              >
                Previous
              </button>

              <span style={styles.pageIndicator}>
                Page {currentPage} of {totalPages}
              </span>

              <button
                disabled={currentPage >= totalPages}
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                style={{
                  ...styles.pageBtn,
                  opacity: currentPage >= totalPages ? 0.4 : 1,
                  cursor: currentPage >= totalPages ? "not-allowed" : "pointer",
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* CONFIRMATION DELETE MODAL */}
      {deleteModalItem && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <div style={styles.modalWarningIcon}>
                <AlertTriangle size={24} color="#dc2626" />
              </div>
              <div>
                <h3 style={styles.modalTitle}>Confirm Resubscribe</h3>
                <p style={styles.modalSubtitle}>
                  Remove from unsubscribed suppression list
                </p>
              </div>
            </div>

            <div style={styles.modalBody}>
              <p style={{ fontSize: "14px", color: "#374151", margin: "0 0 12px 0" }}>
                Are you sure you want to remove{" "}
                <strong style={{ color: "#111827" }}>
                  {deleteModalItem.email}
                </strong>{" "}
                from the unsubscribe list?
              </p>
              <div style={styles.modalInfoBox}>
                <p style={{ margin: 0, fontSize: "13px", color: "#4b5563" }}>
                  💡 <strong>What happens:</strong> The email suppression will be deleted.
                  This recipient will once again be able to receive OTPs, marketing updates,
                  and product notifications from EquipMedy.
                </p>
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                onClick={() => setDeleteModalItem(null)}
                style={styles.btnCancel}
                disabled={deletingId !== null}
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                style={styles.btnConfirmDelete}
                disabled={deletingId !== null}
              >
                {deletingId !== null ? "Removing..." : "Yes, Remove & Resubscribe"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADD UNSUBSCRIBE MODAL */}
      {isAddModalOpen && (
        <div style={styles.modalOverlay}>
          <div style={styles.modalContent}>
            <div style={styles.modalHeader}>
              <div
                style={{
                  ...styles.modalWarningIcon,
                  backgroundColor: "#e0f2f1",
                }}
              >
                <Plus size={24} color="#008080" />
              </div>
              <div>
                <h3 style={styles.modalTitle}>Add Unsubscribed Email</h3>
                <p style={styles.modalSubtitle}>
                  Manually suppress an email address from receiving messages
                </p>
              </div>
            </div>

            <form onSubmit={handleAddEmailSubmit}>
              <div style={styles.modalBody}>
                <label style={styles.inputLabel}>Email Address:</label>
                <input
                  type="email"
                  required
                  placeholder="e.g. customer@example.com"
                  value={newEmailInput}
                  onChange={(e) => setNewEmailInput(e.target.value)}
                  style={styles.textInput}
                  autoFocus
                />
                <p style={{ fontSize: "12px", color: "#6b7280", margin: "6px 0 0 0" }}>
                  This will add the email to the suppression database immediately.
                </p>
              </div>

              <div style={styles.modalFooter}>
                <button
                  type="button"
                  onClick={() => {
                    setIsAddModalOpen(false);
                    setNewEmailInput("");
                  }}
                  style={styles.btnCancel}
                  disabled={submittingAdd}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={styles.btnPrimary}
                  disabled={submittingAdd}
                >
                  {submittingAdd ? "Adding..." : "Add to Unsubscribe List"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// Inline Styles for crisp, modern, polished UI
const styles = {
  container: {
    padding: "24px 28px",
    backgroundColor: "#f9fafb",
    minHeight: "100vh",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    flexWrap: "wrap",
    gap: "16px",
    marginBottom: "24px",
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    marginBottom: "6px",
  },
  iconBadge: {
    width: "40px",
    height: "40px",
    borderRadius: "10px",
    backgroundColor: "#fee2e2",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  title: {
    fontSize: "24px",
    fontWeight: "700",
    color: "#111827",
    margin: 0,
    letterSpacing: "-0.5px",
  },
  badge: {
    backgroundColor: "#fee2e2",
    color: "#b91c1c",
    fontSize: "12px",
    fontWeight: "600",
    padding: "4px 10px",
    borderRadius: "20px",
  },
  subtitle: {
    fontSize: "14px",
    color: "#6b7280",
    margin: "4px 0 0 0",
    maxWidth: "800px",
    lineHeight: "1.5",
  },
  headerActions: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  btnPrimary: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    backgroundColor: "#008080",
    color: "#ffffff",
    border: "none",
    padding: "10px 16px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
    transition: "background-color 0.15s ease",
  },
  btnSecondary: {
    display: "inline-flex",
    alignItems: "center",
    gap: "8px",
    backgroundColor: "#ffffff",
    color: "#374151",
    border: "1px solid #d1d5db",
    padding: "9px 15px",
    borderRadius: "8px",
    fontSize: "13px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.15s ease",
  },
  btnIcon: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "38px",
    height: "38px",
    backgroundColor: "#ffffff",
    color: "#4b5563",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    cursor: "pointer",
  },
  metricsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "16px",
    marginBottom: "20px",
  },
  metricCard: {
    backgroundColor: "#ffffff",
    padding: "18px 20px",
    borderRadius: "10px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.02)",
  },
  metricLabel: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#6b7280",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "6px",
  },
  metricValue: {
    fontSize: "26px",
    fontWeight: "700",
    lineHeight: "1.2",
    marginBottom: "4px",
  },
  metricHelp: {
    fontSize: "12px",
    color: "#9ca3af",
  },
  controlsBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "14px",
    marginBottom: "16px",
  },
  searchWrapper: {
    position: "relative",
    flex: "1",
    maxWidth: "460px",
    minWidth: "260px",
  },
  searchIcon: {
    position: "absolute",
    left: "14px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#9ca3af",
  },
  searchInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 36px 10px 42px",
    fontSize: "14px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
    outline: "none",
  },
  clearSearchBtn: {
    position: "absolute",
    right: "10px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "transparent",
    border: "none",
    color: "#9ca3af",
    cursor: "pointer",
    padding: "4px",
  },
  perPageSelector: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  select: {
    padding: "8px 12px",
    fontSize: "13px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    backgroundColor: "#ffffff",
    color: "#374151",
    cursor: "pointer",
    outline: "none",
  },
  tableCard: {
    backgroundColor: "#ffffff",
    borderRadius: "10px",
    border: "1px solid #e5e7eb",
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.04)",
    overflow: "hidden",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    textAlign: "left",
  },
  thRow: {
    backgroundColor: "#f9fafb",
    borderBottom: "1px solid #e5e7eb",
  },
  th: {
    padding: "14px 18px",
    fontSize: "12px",
    fontWeight: "700",
    color: "#4b5563",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  tr: {
    borderBottom: "1px solid #f3f4f6",
    transition: "background-color 0.15s ease",
  },
  td: {
    padding: "14px 18px",
    fontSize: "14px",
    color: "#1f2937",
    verticalAlign: "middle",
  },
  idBadge: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#4b5563",
    backgroundColor: "#f3f4f6",
    padding: "3px 8px",
    borderRadius: "6px",
    display: "inline-block",
  },
  emailContainer: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  emailText: {
    fontWeight: "600",
    color: "#111827",
    fontSize: "14px",
  },
  userBadge: {
    display: "inline-flex",
    alignItems: "center",
    fontSize: "12px",
    fontWeight: "600",
    color: "#008080",
    backgroundColor: "#e0f2f1",
    padding: "4px 9px",
    borderRadius: "6px",
  },
  guestBadge: {
    fontSize: "12px",
    color: "#9ca3af",
    fontStyle: "italic",
  },
  uidContainer: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  uidText: {
    fontFamily: 'monospace, "Courier New"',
    fontSize: "12px",
    color: "#6b7280",
    maxWidth: "200px",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  iconMiniBtn: {
    background: "transparent",
    border: "none",
    cursor: "pointer",
    padding: "3px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4px",
  },
  unsubscribedTag: {
    fontSize: "11px",
    fontWeight: "700",
    color: "#dc2626",
    backgroundColor: "#fee2e2",
    padding: "4px 9px",
    borderRadius: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    display: "inline-block",
  },
  btnDelete: {
    display: "inline-flex",
    alignItems: "center",
    backgroundColor: "#fee2e2",
    color: "#dc2626",
    border: "1px solid #fca5a5",
    padding: "7px 12px",
    borderRadius: "6px",
    fontSize: "12px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.15s ease",
  },
  paginationBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "14px 18px",
    borderTop: "1px solid #e5e7eb",
    backgroundColor: "#ffffff",
    flexWrap: "wrap",
    gap: "10px",
  },
  pageButtons: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },
  pageBtn: {
    padding: "6px 12px",
    fontSize: "12px",
    fontWeight: "600",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    backgroundColor: "#ffffff",
    color: "#374151",
  },
  pageIndicator: {
    fontSize: "13px",
    color: "#4b5563",
    fontWeight: "500",
  },
  loadingContainer: {
    padding: "60px 20px",
    textAlign: "center",
  },
  spinner: {
    width: "36px",
    height: "36px",
    border: "3px solid #e5e7eb",
    borderTopColor: "#008080",
    borderRadius: "50%",
    animation: "spin 1s linear infinite",
    margin: "0 auto",
  },
  emptyState: {
    padding: "60px 20px",
    textAlign: "center",
  },
  emptyIconCircle: {
    width: "60px",
    height: "60px",
    borderRadius: "50%",
    backgroundColor: "#e0f2f1",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 16px auto",
  },
  emptyTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#111827",
    margin: "0 0 6px 0",
  },
  emptyText: {
    fontSize: "14px",
    color: "#6b7280",
    maxWidth: "400px",
    margin: "0 auto 18px auto",
    lineHeight: "1.5",
  },
  // MODAL STYLES
  modalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.45)",
    backdropFilter: "blur(2px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
    padding: "16px",
  },
  modalContent: {
    backgroundColor: "#ffffff",
    borderRadius: "12px",
    width: "100%",
    maxWidth: "460px",
    boxShadow:
      "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    overflow: "hidden",
  },
  modalHeader: {
    display: "flex",
    alignItems: "center",
    gap: "14px",
    padding: "20px 24px",
    borderBottom: "1px solid #f3f4f6",
  },
  modalWarningIcon: {
    width: "44px",
    height: "44px",
    borderRadius: "50%",
    backgroundColor: "#fee2e2",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  modalTitle: {
    fontSize: "17px",
    fontWeight: "700",
    color: "#111827",
    margin: 0,
  },
  modalSubtitle: {
    fontSize: "12px",
    color: "#6b7280",
    margin: "2px 0 0 0",
  },
  modalBody: {
    padding: "20px 24px",
  },
  modalInfoBox: {
    backgroundColor: "#f9fafb",
    border: "1px solid #e5e7eb",
    borderRadius: "8px",
    padding: "12px 14px",
  },
  inputLabel: {
    display: "block",
    fontSize: "13px",
    fontWeight: "600",
    color: "#374151",
    marginBottom: "6px",
  },
  textInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 14px",
    fontSize: "14px",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    outline: "none",
  },
  modalFooter: {
    display: "flex",
    justifyContent: "flex-end",
    gap: "10px",
    padding: "14px 24px",
    backgroundColor: "#f9fafb",
    borderTop: "1px solid #f3f4f6",
  },
  btnCancel: {
    padding: "9px 16px",
    fontSize: "13px",
    fontWeight: "600",
    backgroundColor: "#ffffff",
    color: "#374151",
    border: "1px solid #d1d5db",
    borderRadius: "8px",
    cursor: "pointer",
  },
  btnConfirmDelete: {
    padding: "9px 18px",
    fontSize: "13px",
    fontWeight: "600",
    backgroundColor: "#dc2626",
    color: "#ffffff",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
  },
};

export default UnsubscribeEmail;
